import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Avatar,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import Rating from '@mui/material/Rating';
import { getSecureImageUrl, handleImageError } from '../../utils/imageUtils';
import { SortableTableCell, useSortableData } from '../TableSortingHelper';

// Utility function to get condition color
const getConditionColor = (condition) => {
  if (!condition) return 'default';
  const c = condition.toLowerCase();
  
  // Excellent/Mint conditions - green
  if (c.includes('mint') || c.includes('sealed') || c.includes('brand new') || c.includes('bnib') || c.includes('new old stock') || c === 'new') {
    return 'success';
  }
  // Good conditions - primary/blue
  if (c.includes('like new') || c.includes('complete') || c.includes('excellent') || c.includes('near mint') || c.includes('very good')) {
    return 'primary';
  }
  // Average conditions - warning/yellow
  if (c.includes('good') || c.includes('acceptable') || c.includes('tested') || c.includes('working') || c === 'used') {
    return 'warning';
  }
  // Poor conditions - error/red
  if (c.includes('damaged') || c.includes('parts') || c.includes('not working') || c.includes('poor') || c.includes('incomplete')) {
    return 'error';
  }
  // Special cases - default
  return 'default';
};

/**
 * Generic table view component for collection/wishlist items
 * @param {Object} props - Component props
 * @param {Array} props.items - Array of items to display
 * @param {function} props.onEdit - Function to handle edit button click
 * @param {function} props.onDelete - Function to handle delete button click
 * @param {string} props.itemType - Type of item ('game', 'console', 'lego')
 * @param {boolean} props.isWishlist - Whether this is a wishlist view
 * @param {function} props.onMoveToCollection - Function to handle moving item to collection (for wishlists)
 * @param {Object} props.columns - Custom column configuration
 * @returns {JSX.Element} Table view component
 */
const TableView = ({
  items,
  onEdit,
  onDelete,
  itemType = 'item',
  isWishlist = false,
  onMoveToCollection,
  onToggleFavourite,
  onToggleBacklog,
  onToggleDNF,
  columns = {}
}) => {
  const { featureFlags } = useFeatureFlags();
  // Add sorting functionality
  const [sortConfig, requestSort, sortedItems] = useSortableData(items);
  const navigate = useNavigate();
  // Default columns configuration
  const getItemDetailUrl = (item) => {
        
    if (itemType === 'game') return `/item/game/${item._id}`;
    if (itemType === 'console') return `/item/console/${item._id}`;
    if (itemType === 'lego') return `/item/lego/${item._id}`;
    if (itemType === 'movie') return `/item/movie/${item._id}`;
    if (itemType === 'music') return `/item/music/${item._id}`;
    if (itemType === 'book') return `/item/book/${item._id}`;
    if (itemType === 'board-game') return `/item/board-game/${item._id}`;
    if (itemType === 'item' && item.format) return `/item/movie/${item._id}`; // Handle movie wishlist items
    return '#';
  };
  
  // Handle item click with proper navigation state
  const handleItemClick = (item) => {
    navigate(getItemDetailUrl(item), { 
      state: { 
        item,
        fromWishlist: isWishlist // Pass the wishlist status to the details page
      } 
    });
  };
  
  const defaultColumns = {
    image: {
      label: 'Image',
      render: (item) => (
        <Avatar 
          variant="rounded"
          sx={{ 
            width: 60, 
            height: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5'
          }}
        >
          {(item.imageUrl || (itemType === 'console' && item.platformLogo)) ? (
            <img 
              src={getSecureImageUrl(itemType === 'console' ? (item.platformLogo || item.imageUrl) : (item.imageUrl || item.set_img_url))}
              alt={item.name}
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'contain',
                padding: '5px'
              }}
              onError={(e) => handleImageError(e, item.name, itemType)}
            />
          ) : (
            (item.name || '?')[0]
          )}
        </Avatar>
      )
    },
    name: {
      label: 'Name',
      render: (item) => (
        <span 
          style={{ cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => handleItemClick(item)}
        >
          {item.name}
        </span>
      )
    },
    actions: {
      label: 'Actions',
      render: (item) => (
        <>
          {item.isSold && (
            <Chip label="Sold" size="small" color="warning" sx={{ mr: 0.5, fontWeight: 700, fontSize: '0.7rem' }} />
          )}
          {onToggleFavourite && !isWishlist && (
            <Tooltip title={(item.favorite || item.favourite || item.isFavorite) ? 'Remove from Favourites' : 'Add to Favourites'}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onToggleFavourite(item); }}
                sx={{ mr: 0.5 }}
              >
                {(item.favorite || item.favourite || item.isFavorite) ? (
                  <FavoriteIcon sx={{ color: '#ff4081' }} fontSize="small" />
                ) : (
                  <FavoriteBorderIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
          {onToggleBacklog && !isWishlist && (
            <Tooltip title={item.inBacklog ? 'Remove from Backlog' : 'Add to Backlog'}>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleBacklog(item); }} sx={{ mr: 0.5 }}>
                {item.inBacklog
                  ? <BookmarkAddedIcon fontSize="small" sx={{ color: '#ce93d8' }} />
                  : <BookmarkAddIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
          {onToggleDNF && !isWishlist && featureFlags.enableDNF === true && (
            <Tooltip title={item.isDNF ? 'Clear DNF' : 'Mark as DNF'}>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleDNF(item); }} sx={{ mr: 0.5 }}>
                <DoNotDisturbOnIcon fontSize="small" sx={{ color: item.isDNF ? 'warning.main' : 'text.disabled' }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={`Edit ${itemType}`}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(item);
              }}
              sx={{ mr: 1 }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={`Remove ${itemType}`}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(item);
              }}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          {isWishlist && onMoveToCollection && (
            <Tooltip title="Add to Collection">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveToCollection(item);
                }}
                color="primary"
              >
                <AddShoppingCartIcon />
              </IconButton>
            </Tooltip>
          )}
        </>
      )
    }
  };

  // Add item-specific default columns
  if (itemType === 'game') {
    defaultColumns.platform = {
      label: 'Platform',
      render: (item) => item.platform
    };
    defaultColumns.priority = {
      label: 'Priority',
      render: (item) => item.priority || 'Medium'
    };
    defaultColumns.condition = {
      label: 'Condition',
      render: (item) => item.condition ? (
        <Chip size="small" label={item.condition} color={getConditionColor(item.condition)} variant="outlined" />
      ) : 'N/A'
    };
    defaultColumns.played = {
      label: 'Played',
      render: (item) => item.played ? (
        <CheckCircleIcon color="success" />
      ) : (
        <CancelIcon color="error" />
      )
    };
    defaultColumns.completed = {
      label: 'Completed',
      render: (item) => item.completed ? (
        <EmojiEventsIcon sx={{ color: 'gold' }} />
      ) : (
        <CancelIcon color="error" />
      )
    };
  } else if (itemType === 'console') {
    defaultColumns.condition = {
      label: 'Condition',
      render: (item) => item.condition ? (
        <Chip size="small" label={item.condition} color={getConditionColor(item.condition)} variant="outlined" />
      ) : 'N/A'
    };
    defaultColumns.generation = {
      label: 'Generation',
      render: (item) => item.generation || 'N/A'
    };
    defaultColumns.year = {
      label: 'Year',
      render: (item) => item.year || 'N/A'
    };
  } else if (itemType === 'lego') {
    defaultColumns.setNum = {
      label: 'Set #',
      render: (item) => {
        const setNum = item.setNum || item.set_num;
        return setNum ? setNum.split('-')[0] : 'N/A';
      }
    };
    defaultColumns.year = {
      label: 'Year',
      render: (item) => item.year || 'N/A'
    };
    defaultColumns.theme = {
      label: 'Theme',
      render: (item) => item.theme || 'N/A'
    };
    defaultColumns.condition = {
      label: 'Condition',
      render: (item) => item.condition ? (
        <Chip size="small" label={item.condition} color={getConditionColor(item.condition)} variant="outlined" />
      ) : 'N/A'
    };
  } else if (itemType === 'movie') {
    defaultColumns.format = {
      label: 'Format',
      render: (item) => item.format || 'N/A'
    };
    defaultColumns.year = {
      label: 'Year',
      render: (item) => item.year || 'N/A'
    };
    defaultColumns.condition = {
      label: 'Condition',
      render: (item) => item.condition ? (
        <Chip size="small" label={item.condition} color={getConditionColor(item.condition)} variant="outlined" />
      ) : 'N/A'
    };
    defaultColumns.watched = {
      label: 'Watched',
      render: (item) => item.watched ? (
        <CheckCircleIcon color="success" />
      ) : (
        <CancelIcon color="error" />
      )
    };
  } else if (itemType === 'music') {
    defaultColumns.artist = {
      label: 'Artist',
      render: (item) => item.artist || 'N/A'
    };
    defaultColumns.format = {
      label: 'Format',
      render: (item) => item.format || 'N/A'
    };
    defaultColumns.year = {
      label: 'Year',
      render: (item) => item.year || 'N/A'
    };
    defaultColumns.condition = {
      label: 'Condition',
      render: (item) => item.condition ? (
        <Chip size="small" label={item.condition} color={getConditionColor(item.condition)} variant="outlined" />
      ) : 'N/A'
    };
    defaultColumns.listened = {
      label: 'Listened',
      render: (item) => item.listened ? (
        <CheckCircleIcon color="success" />
      ) : (
        <CancelIcon color="error" />
      )
    };
  } else if (itemType === 'book') {
    defaultColumns.author = {
      label: 'Author',
      render: (item) => item.author || 'N/A'
    };
    defaultColumns.format = {
      label: 'Format',
      render: (item) => item.format || 'N/A'
    };
    defaultColumns.year = {
      label: 'Year',
      render: (item) => item.year || 'N/A'
    };
    defaultColumns.genre = {
      label: 'Genre',
      render: (item) => item.genre || 'N/A'
    };
    defaultColumns.condition = {
      label: 'Condition',
      render: (item) => item.condition ? (
        <Chip size="small" label={item.condition} color={getConditionColor(item.condition)} variant="outlined" />
      ) : 'N/A'
    };
    defaultColumns.read = {
      label: 'Read',
      render: (item) => item.read ? (
        <CheckCircleIcon color="success" />
      ) : (
        <CancelIcon color="error" />
      )
    };
  } else if (itemType === 'board-game') {
    defaultColumns.players = {
      label: 'Players',
      render: (item) => {
        if (item.minPlayers && item.maxPlayers) {
          return item.minPlayers === item.maxPlayers ? `${item.minPlayers}` : `${item.minPlayers}-${item.maxPlayers}`;
        }
        return 'N/A';
      }
    };
    defaultColumns.playingTime = {
      label: 'Time',
      render: (item) => item.playingTime ? `${item.playingTime} min` : 'N/A'
    };
    defaultColumns.year = {
      label: 'Year',
      render: (item) => item.year || 'N/A'
    };
    defaultColumns.condition = {
      label: 'Condition',
      render: (item) => item.condition ? (
        <Chip size="small" label={item.condition} color={getConditionColor(item.condition)} variant="outlined" />
      ) : 'N/A'
    };
    defaultColumns.played = {
      label: 'Played',
      render: (item) => item.played ? (
        <CheckCircleIcon color="success" />
      ) : (
        <CancelIcon color="error" />
      )
    };
  }

  // Rating column — common to all types
  defaultColumns.rating = {
    label: 'Rating',
    render: (item) => item.rating ? (
      <Rating value={item.rating} readOnly size="small" />
    ) : '—'
  };

  // Merge default columns with custom columns
  const mergedColumns = { ...defaultColumns, ...columns };

  // Get array of column keys in the desired order
  const columnOrder = ['image', 'name'];

  if (itemType === 'game') {
    columnOrder.push('platform', 'priority', 'condition', 'played', 'completed', 'rating');
  } else if (itemType === 'console') {
    columnOrder.push('condition', 'generation', 'year', 'rating');
  } else if (itemType === 'lego') {
    columnOrder.push('setNum', 'year', 'theme', 'condition', 'rating');
  } else if (itemType === 'movie') {
    columnOrder.push('format', 'year', 'condition', 'watched', 'rating');
  } else if (itemType === 'music') {
    columnOrder.push('artist', 'format', 'year', 'condition', 'listened', 'rating');
  } else if (itemType === 'book') {
    columnOrder.push('author', 'format', 'year', 'genre', 'condition', 'read', 'rating');
  } else if (itemType === 'board-game') {
    columnOrder.push('players', 'playingTime', 'year', 'condition', 'played', 'rating');
  }

  columnOrder.push('actions');

  // Filter column order to only include columns that exist in mergedColumns
  const finalColumnOrder = columnOrder.filter(key => mergedColumns[key]);

  return (
    <TableContainer component={Paper} sx={{ mt: 4 }}>
      <Table>
        <TableHead>
          <TableRow>
            {finalColumnOrder.map(key => (
              key === 'image' || key === 'actions' ? (
                <TableCell key={key}>{mergedColumns[key].label}</TableCell>
              ) : (
                <SortableTableCell
                  key={key}
                  id={key}
                  label={mergedColumns[key].label}
                  sortConfig={sortConfig}
                  onRequestSort={requestSort}
                />
              )
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedItems.map((item) => (
            <TableRow 
              key={item._id}
              hover
              onClick={() => {
                // Add isWishlistItem property to the item object itself
                const itemWithWishlistFlag = {
                  ...item,
                  isWishlistItem: isWishlist
                };
                navigate(getItemDetailUrl(item), { 
                  state: { 
                    item: itemWithWishlistFlag, 
                    fromWishlist: isWishlist 
                  } 
                });
              }}
              sx={{ cursor: 'pointer' }}
            >
              {finalColumnOrder.map(key => (
                <TableCell key={`${item._id}-${key}`}>
                  {mergedColumns[key].render(item)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TableView;
